import { FC, cloneElement, isValidElement } from 'react';
import {
  Box,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import styles from './PillSelectField.module.scss';
import iconStyles from '../discoverFieldIcons.module.scss';

export type PillSelectOption = { value: string; label: string };

type PillSelectFieldProps = {
  name: string;
  inputLabel: string;
  value: string;
  onChange: (e: SelectChangeEvent<string>) => void;
  options: PillSelectOption[];
  error?: string;
  startIcon?: React.ReactNode;
  compactForMobile?: boolean;
  allowClear?: boolean;
  clearValue?: string;
  placeholder?: string;
};

const PillSelectField: FC<PillSelectFieldProps> = ({
  name,
  inputLabel,
  value,
  onChange,
  options,
  error,
  startIcon,
  compactForMobile = false,
  allowClear = false,
  clearValue = '',
  placeholder,
}) => {
  const withIconClass = (node: React.ReactNode) => {
    if (isValidElement<{ className?: string }>(node)) {
      const { className: existingClass } = node.props;
      return cloneElement(node, {
        className: [iconStyles.svgIcon, existingClass].filter(Boolean).join(' '),
      });
    }
    return node;
  };

  const startAdornment =
    startIcon != null ? (
      <InputAdornment position="start" className={iconStyles.adornmentTight}>
        {withIconClass(startIcon)}
      </InputAdornment>
    ) : undefined;

  const showClear = allowClear && value !== clearValue;

  const labelForSelected = (selected: string) => {
    const hit = options.find(o => o.value === selected);
    return hit?.label ?? selected;
  };

  const renderValue =
    placeholder !== undefined
      ? (selected: unknown) => {
          const s = typeof selected === 'string' ? selected : String(selected ?? '');
          if (s === '' || s === clearValue) {
            return (
              <Typography
                component="span"
                variant="h11"
                sx={{ color: '#5E6A81', fontWeight: 400 }}
              >
                {placeholder}
              </Typography>
            );
          }
          return labelForSelected(s);
        }
      : undefined;

  const endClear = showClear ? (
    <InputAdornment
      position="end"
      className={styles.clearAdornment}
    >
      <IconButton
        type="button"
        size="small"
        aria-label="Clear"
        edge="end"
        className={styles.clearButton}
        onMouseDown={e => e.preventDefault()}
        onClick={() => {
          onChange({
            target: { name, value: clearValue },
          } as SelectChangeEvent<string>);
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </InputAdornment>
  ) : undefined;

  return (
    <Box
      className={styles.root}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.label}>
        <Typography variant="h11Semibold" color="#222B3A">{inputLabel}</Typography>
      </FormLabel>
      <Select
        name={name}
        value={value}
        onChange={onChange}
        displayEmpty
        renderValue={renderValue}
        error={!!error}
        fullWidth
        className={`${styles.select} ${startAdornment ? styles.selectWithLeadIcon : ''} ${
          showClear ? styles.selectWithClear : ''
        }`.trim()}
        IconComponent={ExpandMoreIcon}
        MenuProps={{ className: styles.menuPaper }}
        startAdornment={startAdornment}
        endAdornment={endClear}
      >
        {options.map(opt => (
          <MenuItem
            key={opt.value === '' ? '__empty' : opt.value}
            value={opt.value}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      <Box
        className={styles.errorSlot}
        role={error ? 'alert' : undefined}
      >
        {error ? (
          <Typography
            variant="h11"
            className={styles.errorText}
          >
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default PillSelectField;
